export default {
  name: "BolideAssistant",

  props: {
    is_open: Boolean,
    tiles: Array,
    activeProperty: Object,
    user: Object,
  },

  emits: ["close-assistant"],

  data() {
    return ({
      user_message: "",
      messages: [],
    });
  },

  watch: {
    // Auto-scroll chat when messages change
    messages() {
      this.$nextTick(() => {
        const el = this.$refs.chat_body;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },

    // Lock body scroll when assistant is open
    is_open(isOpen) {
      document.body.classList.toggle("chatbot-lock", isOpen);
      if (isOpen && this.messages.length === 0) {
        this.initGreeting();
      }
    },
  },

  mounted() {
    // Click outside to close
    document.addEventListener("mousedown", this.handleClickOutside);
    document.addEventListener("touchstart", this.handleClickOutside);

    if (this.is_open) {
      this.initGreeting();
    }
  },

  beforeUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    document.removeEventListener("touchstart", this.handleClickOutside);
    document.body.classList.remove("chatbot-lock");
  },

  methods: {
    /* ===============================
       UI CONTROL
    =============================== */

    initGreeting() {
      const name = this.user?.first_name || "";
      const greeting = name ? `Hello ${name}! I'm Infinity Assist. How can I help you today?` : "Hello! I'm Infinity Assist. How can I help you today?";
      this.messages = [{ sender: "assistant", text: greeting }];
    },

    close_assistant() {
      this.$emit("close-assistant");
    },

    handleClickOutside(event) {
      if (!this.is_open) return;

      const panel = this.$refs.chatbot;
      if (panel && !panel.contains(event.target)) {
        this.close_assistant();
      }
    },

    /* ===============================
       CHAT LOGIC
    =============================== */

    send() {
      if (!this.user_message.trim()) return;

      const text = this.user_message.trim();

      // User message
      this.messages.push({ sender: "user", text });

      this.user_message = "";

      // Simulated AI delay
      setTimeout(() => {
        this.generateReply(text.toLowerCase());
      }, 350);
    },

    reply(text) {
      this.messages.push({ sender: "assistant", text });
    },

    generateReply(q) {
      const p = this.activeProperty;

      // User Identification
      if (q.includes("who am i") || q.includes("my name")) {
        const name = this.user?.first_name || "a user";
        return this.reply(`You are ${name}. You are currently logged into the Infinity Dashboard.`);
      }

      if (!p) {
        return this.reply("I'm Infinity Assist, your AI dashboard companion. Please select a property (like 'Infinity') from the sidebar so I can help you with specific details about that location.");
      }

      // 1. CRIME STATISTICS
      if (q.includes("crime") || q.includes("security") || q.includes("precinct")) {
        const stats = p.crimeStats;
        if (stats) {
          let text = `Security overview for ${p.title} (${stats.precinct}): The crime index is currently ${stats.crimeIndex}. `;
          if (stats.periodIncrease) text += `There has been a ${stats.periodIncrease}. `;
          if (stats.incidentSummary && stats.incidentSummary.length) {
            text += "\n\nRecent incident summary:";
            stats.incidentSummary.forEach(inc => {
              text += `\n- ${inc.category}: ${inc.count} incidents (${inc.trend} trend)`;
            });
          }
          return this.reply(text);
        }
        return this.reply(`I don't have detailed crime statistics for ${p.title} at the moment.`);
      }

      // 2. MANAGEMENT / ON-SITE TEAM
      if (q.includes("manager") || q.includes("management") || q.includes("contact") || q.includes("team")) {
        const mgmtCard = p.tileCards?.find(c => c.type === "details" && (c.title?.toLowerCase().includes("team") || c.title?.toLowerCase().includes("management")));
        if (mgmtCard && mgmtCard.contacts && mgmtCard.contacts.length) {
          let text = `Here are the on-site management contacts for ${p.title}:`;
          mgmtCard.contacts.forEach(c => {
            text += `\n\n**${c.full_name}** (${c.role})\nStatus: ${c.status}\nPhone: ${c.phone}\nEmail: ${c.email}`;
          });
          return this.reply(text);
        }
        return this.reply(`I couldn't find specific management contacts for ${p.title}.`);
      }

      // 3. SERVICE PROVIDERS
      if (q.includes("provider") || q.includes("service") || q.includes("utility")) {
        const providerCard = p.tileCards?.find(c => c.type === "service-providers");
        if (providerCard && providerCard.providers && providerCard.providers.length) {
          let text = `Service providers for ${p.title}:`;
          providerCard.providers.forEach(sp => {
            text += `\n- **${sp.name}** (${sp.category}): ${sp.phone}`;
          });
          return this.reply(text);
        }
        return this.reply(`I don't have a list of service providers for ${p.title} currently.`);
      }

      // 4. GENERAL HELP
      this.reply(
        `I can help you with information about ${p.title}. Try asking about:\n- **Crime & Security**: 'What are the crime stats?'\n- **Management**: 'Who is the centre manager?'\n- **Service Providers**: 'List the utility providers.'`
      );
    },
  },
};
